/**
 * SectionCard — gradient-bordered section card matching the real genie detail
 * page (icon chip header + title/subtitle + optional trailing + body slot).
 *
 * Brand law: signature gradient border, GenieIcon for the header chip, brand
 * tokens only.
 */
import type { ReactNode } from "react";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface SectionCardProps {
  genieIcon: GenieIconName;
  /** Tint for the icon + chip background (brand token hex) */
  tint?: string;
  title: string;
  subtitle?: string;
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
}

export const SectionCard = ({
  genieIcon,
  tint = "#4E9CFF",
  title,
  subtitle,
  trailing,
  children,
  className,
}: SectionCardProps) => {
  return (
    <div className={`p-[1.5px] rounded-[18px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm ${className ?? ""}`}>
      <div className="rounded-[16.5px] bg-white overflow-hidden">
        <div className="flex items-center gap-3 px-5 md:px-6 py-4 border-b border-[#F3F4F6]">
          <div
            className="w-10 h-10 rounded-[11px] flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${tint}1a` }}
          >
            <GenieIcon name={genieIcon} size={20} color={tint} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[17px] font-extrabold text-[#222B45] tracking-[-0.3px]">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          {trailing}
        </div>
        <div className="px-5 md:px-6 pt-4 pb-5">{children}</div>
      </div>
    </div>
  );
};

export default SectionCard;
